import "./crash/install_first.js";
import { BackgroundWork } from "./utils/background_work.js";
import { CrashReporter, crashCapture } from "./crash/crash_reporter.js";
import { CrashIdentity } from "./crash/identity.js";
import { ensureDeviceId } from "./utils/device_id.js";
import { SimulationRunner } from "./simulation/simulation_runner.js";
import { TestLevelBridge } from "./debug/test_level_bridge.js";
import { exposeStabilityCheck } from "./debug/stability_check.js";
import { LevelPoolBrowser } from "./debug/level_pool_browser.js";

// Dev helper: jump straight to a screen from the browser console.
( window as any ).showScreen = ( name: string ) =>
{
  void ScreenManager.showScreen( name as Screens );
};
( window as any ).runSim = ( level: number, runs = 1000 ) =>
  new SimulationRunner().run( level, runs );

( window as any ).runRange = ( from: number, to: number, runs = 300 ) =>
  new SimulationRunner().runRange( from, to, runs );

( window as any ).currentUi = () => ScreenManager.getCurrentUi();
( window as any ).UiTransforms = { TransformScale, TransformStraightMove, TransformOpacity, TransformRotate, Easing, Vector2 };

exposeStabilityCheck();
LevelPoolBrowser.expose();

let late_consent_applied = false;

function applyLateConsent(): void
{
  if ( late_consent_applied || !S2SEventContextService.getConsentSnapshot().analytics_allowed ) return;
  late_consent_applied = true;
  CrashIdentity.setConsentAllowed( true );
  console.info( '[Startup] crash context enabled after late consent' );
}

import { Game } from "./game.js";
import { LocalizationManager } from "./localization/localization_manager.js";
import { TelegramAPISettings } from "./telegram_api_settings.js";
import { performLogin } from "./api/perform_login.js";
import { reportSessionStart, reportLevelActive } from "./api/requests/economy.requests.js";
import { syncAppsflyerAttribution } from "./analytics/appsflyer_attribution.js";
import { ensureUserId } from "./analytics/analytics_identity.js";
import { NetworkError, setOnUnauthorized } from "./api/http_client.js";
import { analytics, AnalyticsEventName, AnalyticsPropertyKey, initAnalytics, trackWebEvent } from "./analytics/index.js";
import { OfferService } from "./offers/offer_service.js";
import { logAfAppOpen, logAfSessionMinuteMilestone } from "./analytics/appsflyer_events.js";
import * as MaxAds from "./ads/applovin_max.js";
import { preloadInterstitialInBackground } from "./ads/first_load_manager.js";
import { S2SEventContextService } from "./s2s/s2s_event_context.js";
import { AdsConfigService } from "./ads/ads_config.js";
import { AppConfigService } from "./app_config/app_config_service.js";
import { LevelPackService } from "./level_pack/level_pack_service.js";
import { CannonConfigService } from "./cannon_config/cannon_config_service.js";
import { EventsConfigService } from "./events_config/events_config_service.js";
import { areBundledLevelsAllowed } from "./level_config.js";
import { maybeShowUpdateDialog } from "./app_config/app_update_check.js";
import { VersionLoader } from "./version_loader.js";
import { ScreenManager, Screens } from "./screen_manager/screen_manager.js";
import { TransformScale } from "./transforms/transform_scale.js";
import { TransformStraightMove } from "./transforms/transform_straight_move.js";
import { TransformOpacity } from "./transforms/transform_opacity.js";
import { TransformRotate } from "./transforms/transform_rotate.js";
import { Easing } from "./transforms/transform_base.js";
import { Vector2 } from "./common/vector2.js";
import { DebugConsoleManager } from "./debug/debug_console.js";
import { DebugSettings, GlobalSettings } from "./global_settings.js";
import { LogOverlay } from "./debug/log_overlay.js";
import { audioController } from "./audio/audio_controller.js";
import { hapticsController, VibrationContext } from "./haptics/haptics_controller.js";
import { HapticPattern } from "./haptics/haptics_constants.js";
import { StatisticsStorage } from "./storage/statistics_storage.js";
import { nextHeartAtMs } from "./api/player_sync.js";
import { SocketHandler } from "./socket_handler/socket_handler.js";
import { NetworkManager } from "./network_manager.js";
import { SessionController } from "./socket_handler/controllers/session_controller.js";
import { AppVersionController } from "./socket_handler/controllers/app_version_controller.js";
import { ServerRoutingService } from "./server_routing/server_routing_service.js";
import { NotificationManager } from "./notifications/notification_manager.js";
import { NotificationConfigService } from "./notifications/notification_config.js";
import { OfferTriggerController } from "./offers/offer_trigger_controller.js";
import { DailyTasksService } from "./daily_tasks/daily_tasks_service.js";
import { OfflineMode } from "./offline/offline_mode.js";
import { AuthSessionStore } from "./api/auth_session.js";
import { bindAppsFlyerCustomerUserId } from "./analytics/appsflyer_customer_user_id.js";
import { OfflineSyncService } from "./offline/offline_sync_service.js";
import { OfflineEconomy, OFFLINE_ECONOMY_CONFIG_TYPES } from "./offline/offline_economy.js";
import { OfflineJournal } from "./offline/offline_journal.js";
import { LeaderboardSyncService } from "./api/leaderboard/leaderboard_sync_service.js";
import { ConfigSyncService } from "./config_sync/config_sync_service.js";
import { CumulativeBoosterService } from "./cumulative_boosters/cumulative_booster_service.js";
import { ConfigHashStore } from "./config_sync/config_hash_store.js";
import { fetchBootstrap, primeFromBootstrap } from "./api/requests/bootstrap.requests.js";
import { PrimedResponses } from "./api/primed_responses.js";
import { TransportFlags } from "./api/transport_flags.js";
import { isConfigMissing } from "./utils/network_errors.js";
import { LocalMetrics } from "./offline/local_metrics.js";
import * as EconomyGateway from "./offline/economy_gateway.js";
import { DailyChallengeService } from "./daily_challenge/daily_challenge_service.js";
import { WinStreakTowerService } from "./win_streak_tower/win_streak_tower_service.js";
import { linkProvider, isProviderAvailable, currentAccountProvider } from "./api/account_link_service.js";
import { LinkedSocialProvider, fromAuthProvider, getLinkedSocialProviders } from "./ui/dialog/account_providers_preview_state.js";
import { SaveDialog } from "./ui/dialog/auth_dialogs/save_dialog.js";
import { AccountProgressSavedDialog } from "./ui/dialog/auth_dialogs/account_progress_saved_dialog.js";
import { TwoAccountsDialog } from "./ui/dialog/auth_dialogs/two_accounts_dialog.js";
import { AuthMessageDialog } from "./ui/dialog/auth_dialogs/auth_message_dialog.js";
import { AuthProvider } from "./api/dto/auth.dto.js";
import { DialogManager } from "./ui/dialog/dialog_manager.js";
import { KeyHuntService } from "./key_hunt/key_hunt_service.js";
import { MenuScreen } from "./ui/screens/menu_screen/menu_screen.js";
import { TournamentService } from "./tournament/tournament_service.js";
import { MemoryProbe } from "./debug/memory_probe.js";
import { MemoryDashboard } from "./debug/memory_dashboard.js";
import { UiPerfProbe } from "./debug/ui_perf_probe.js";
import { PerfSession } from "./debug/perf_session.js";
import { GraphicsProfile, GraphicsQuality, GraphicsSettings } from "./graphics/graphics_quality.js";
import { GraphicsConfigService } from "./graphics/graphics_config_service.js";
import { MenuLayoutConfigService } from "./menu_layout/menu_layout_config_service.js";
import { PrizeShotService } from "./prize_shot/prize_shot_service.js";
import { DEFAULT_MENU_LAYOUT, normalizeMenuLayout } from "./ui/screens/menu_screen/menu_side_slots.js";
import { normalizeGraphicsConfig } from "./graphics/graphics_remote_config.js";
import { Renderer } from "./core/renderer.js";
import { SceneManager } from "./core/scene_manager.js";
import { CannonBall } from "./entities/cannon_ball.js";
import { StartupProgress } from "./ui/screens/loading_screen/startup_progress.js";
// import { DevToDev } from "./plugins/dev_to_dev_plugin.js";

// import { VConsoleManager } from "./vconsole_manager.js";
// import { GlobalSettings } from "./global_settings.js";

// if ( GlobalSettings.IS_PROD )
// {
//   console.log = () => {};
//   console.warn = () => {};
//   console.info = () => {};
//   console.debug = () => {};
// }

// if ( window.Capacitor )
// {
//   // @ts-ignore  //Ця ліба опціональна і не потрібна для веба, тому щоб компілятор не сварився коли її немає - юзаємо  ts-ignore
//   import( '@capacitor/app' ).then( ( { App } ) =>
//   {
//     if ( window.Capacitor && window.Capacitor.Plugins )
//     {
//       window.Capacitor.Plugins.App = App;
//     }
//   } ).catch( error =>
//   {
//     console.error( 'Failed to import Capacitor App plugin:', error );
//     console.error( 'Не забудь встановити ліби - npm i :');
//   } );
//
//   // @ts-ignore //Ця ліба опціональна і не потрібна для веба, тому щоб компілятор не сварився коли її немає - юзаємо  ts-ignore
//   import( '@capacitor/dialog' ).then( ( { Dialog } ) =>
//   {
//     if ( window.Capacitor && window.Capacitor.Plugins )
//     {
//       window.Capacitor.Plugins.Dialog = Dialog;
//     }
//   } ).catch( error =>
//   {
//     console.error( 'Failed to import Capacitor Dialog plugin:', error );
//     console.error( 'Не забудь встановити ліби - npm i :');
//   } );
//
//   // @ts-ignore
//   import( '@capacitor/haptics' ).then( ( { Haptics } ) =>
//   {
//     if ( window.Capacitor && window.Capacitor.Plugins )
//     {
//       window.Capacitor.Plugins.Haptics = Haptics;
//     }
//   } ).catch( error =>
//   {
//     console.error( 'Failed to import Capacitor Haptics plugin:', error );
//     console.error( 'Не забудь встановити ліби - npm i :');
//   } );
// }

function preventCollapse() // vertical scroll bug fix (Telegram issue)
{
  document.body.style.overflowY = 'hidden'
  document.body.style.height = ( window.innerHeight + Game.getYOverflow() ) + "px"
  window.scrollTo( 0, Game.getYOverflow() );
}

const SESSION_MILESTONE_MINUTES = [ 1, 2, 4, 6, 10, 15 ] as const;
let session_elapsed_ms = 0;
let session_last_tick_ms: number | null = null;
const sent_session_milestones = new Set<number>();
let is_session_timer_paused = document.hidden;
let is_app_backgrounded = document.hidden;
let backgrounded_at_ms: number | null = null;
let foregrounded_at_ms = Date.now();

const SESSION_RESUME_THRESHOLD_MS = 30_000;

const reportSessionToServer = ( settle_abandoned = false ): void =>
{
  LocalMetrics.resetSessionStreak();
  if ( !NetworkManager.isOnline() )
    return;

  void OfflineSyncService.sync( 'session_start' )
    .then( () => reportSessionStart( settle_abandoned ) )
    .then( result =>
    {
      if ( OfflineJournal.hasPending() )
        return;
      StatisticsStorage.update( {
        hearts: result.hearts,
        max_hearts: result.max_hearts,
        next_heart_at: nextHeartAtMs( result.next_heart_at, 0 ),
      } );
      void OfferTriggerController.report( 'session_started' );
    } )
    .catch( e => console.warn( '[Economy] session-start failed', e ) );
};

const pauseSessionMilestoneTracking = (): void =>
{
  is_session_timer_paused = true;
  session_last_tick_ms = null;
};

const resumeSessionMilestoneTracking = (): void =>
{
  is_session_timer_paused = false;
  session_last_tick_ms = null;
};

const reportLevelActivity = ( active: boolean ): void =>
{
  if ( ScreenManager.getCurrentScreen() !== Screens.GAME ) return;

  void reportLevelActive( active ).catch( e => console.warn( '[Economy] level-active failed', e ) );
};

const handleAppBackground = (): void =>
{
  if ( is_app_backgrounded ) return;
  is_app_backgrounded = true;
  backgrounded_at_ms = Date.now();
  CrashReporter.checkpoint( 'backgrounded' );
  pauseSessionMilestoneTracking();
  reportLevelActivity( false );
};

const handleAppForeground = (): void =>
{
  if ( !is_app_backgrounded ) return;
  is_app_backgrounded = false;
  foregrounded_at_ms = Date.now();
  CrashReporter.checkpoint( 'foreground' );
  resumeSessionMilestoneTracking();
  void ServerRoutingService.checkIfStale( 'foreground' );
  void logAfAppOpen( 'unknown' );
  reportLevelActivity( true );
  void OfflineSyncService.sync( 'foreground' );
  void ConfigSyncService.refreshStale( 'foreground', { throttle: true } );
  void OfferService.refreshIfScheduleChanged();
  preloadInterstitialInBackground();

  if ( backgrounded_at_ms != null && Date.now() - backgrounded_at_ms >= SESSION_RESUME_THRESHOLD_MS )
    reportSessionToServer();
  backgrounded_at_ms = null;
};

const tickSessionMilestones = ( now_ms: number ): void =>
{
  if ( is_session_timer_paused ) return;
  if ( session_last_tick_ms == null )
  {
    session_last_tick_ms = now_ms;
    return;
  }

  const delta_ms = Math.max( 0, now_ms - session_last_tick_ms );
  session_last_tick_ms = now_ms;
  session_elapsed_ms += delta_ms;

  for ( const minute of SESSION_MILESTONE_MINUTES )
  {
    if ( sent_session_milestones.has( minute ) ) continue;
    if ( session_elapsed_ms < minute * 60_000 ) continue;

    sent_session_milestones.add( minute );
    void logAfSessionMinuteMilestone( minute );
    analytics.track( AnalyticsEventName.SESSION_MIN, {
      [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
      [AnalyticsPropertyKey.SESSIONMIN]: minute
    } );
  }
};

const SESSION_MILESTONE_TICK_MS = 1000;

const startSessionMilestoneLoop = (): void =>
{
  setInterval( () => tickSessionMilestones( performance.now() ), SESSION_MILESTONE_TICK_MS );
};

function syncBannerVisibilityWithCurrentScreen(): void
{
  const current_screen = ScreenManager.getCurrentScreen();
  MaxAds.setBannerInitialDelay( AdsConfigService.get().banner.initial_delay_seconds * 1000 );
  MaxAds.setBannerVisible( current_screen !== Screens.GAME );
}

function registerConfigSync(): void
{
  ConfigSyncService.register( [ 'ads' ], () => AdsConfigService.refresh(), () => AdsConfigService.hasCachedConfig() );
  ConfigSyncService.register( [ 'app', 'booster_highlight_config' ], () => AppConfigService.refresh(), type => AppConfigService.hasCachedConfig( type ) );
  ConfigSyncService.register( [ 'cannon' ], () => CannonConfigService.refresh(), () => CannonConfigService.hasCachedConfig() );
  ConfigSyncService.register( [ 'events_unlock' ], () => EventsConfigService.refresh(), () => EventsConfigService.hasCachedConfig() );
  ConfigSyncService.register( [ 'graphics' ], () => GraphicsConfigService.refresh(), () => GraphicsConfigService.hasCachedConfig() );
  ConfigSyncService.register( [ 'menu_layout' ], () => MenuLayoutConfigService.refresh(), () => MenuLayoutConfigService.hasCachedConfig() );
  ConfigSyncService.register( [ 'prize_shot' ], () => PrizeShotService.refreshConfig(), () => PrizeShotService.hasCachedConfig() );
  ConfigSyncService.register( [ 'notifications' ], () => NotificationConfigService.refresh(), () => NotificationConfigService.hasCachedConfig() );
  ConfigSyncService.register( [ ...OFFLINE_ECONOMY_CONFIG_TYPES ], stale => OfflineEconomy.refreshConfigs( stale ), type => OfflineEconomy.hasCachedConfig( type ) );
  ConfigSyncService.register( [ 'boosters_prices' ], () => CumulativeBoosterService.refreshPricesConfig(), type => CumulativeBoosterService.hasCachedConfig( type ) );
  ConfigSyncService.register( [ 'boosters_properties' ], () => CumulativeBoosterService.refreshPropertiesConfig(), type => CumulativeBoosterService.hasCachedConfig( type ) );
  ConfigSyncService.register( [ 'level_pack' ], () => LevelPackService.refresh(), () => LevelPackService.hasCachedRelease() );
}

const FRESH_FETCH_MS = 60_000;
const BOOTSTRAP_RETRY_MS = 60_000;

class BootstrapIncompleteError extends Error
{
  constructor( missing: string[] )
  {
    super( `[Startup] first online bootstrap incomplete: ${missing.join( ', ' )}` );
    this.name = 'BootstrapIncompleteError';
  }
}

// One POST replaces the config-hashes, stale content-config and three event
// GETs that used to run in sequence after login. The responses are parked in
// PrimedResponses and consumed by the very same request functions the rest of
// the startup calls, so nothing downstream knows the difference. Any failure
// simply leaves the old per-request path in place.
async function primeStartupFromBootstrap(): Promise<void>
{
  PrimedResponses.clear();
  if ( !NetworkManager.isOnline() ) return;
  try
  {
    const payload = await fetchBootstrap( ConfigSyncService.knownHashes(), ConfigSyncService.registeredTypes() );
    const primed = primeFromBootstrap( payload );
    console.info( `[Bootstrap] primed ${primed} startup response(s), ${Object.keys( payload.configs ?? {} ).length} config(s) stale` );
  }
  catch ( e )
  {
    if ( isConfigMissing( e ) )
      console.info( '[Bootstrap] server has no bootstrap route, using separate requests' );
    else
      console.warn( '[Bootstrap] failed, using separate requests', e );
  }
}

async function performLoginAndRefreshAdsConfig(): Promise<void>
{
  try { await performLoginAndRefreshAdsConfigInner(); }
  finally { PrimedResponses.clear(); }
}

async function performLoginAndRefreshAdsConfigInner(): Promise<void>
{
  const first_bootstrap = !OfflineMode.isBootstrapped();
  const missing: string[] = [];

  const logged_in = await performLogin();
  StartupProgress.netStep( 'login' );
  if ( !logged_in || !AuthSessionStore.isLoggedIn() )
    throw new NetworkError( 'login failed' );
  CrashIdentity.setUser( AuthSessionStore.getPlayerId() );
  CrashReporter.checkpoint( 'logged_in' );
  await SessionController.sendStartSession();
  await primeStartupFromBootstrap();
  StartupProgress.netStep( 'bootstrap' );
  const configs = await ConfigSyncService.refreshStale( 'startup' );
  StartupProgress.netStep( 'configs' );
  CrashReporter.checkpoint( 'configs_ready' );
  if ( !configs.checked ) missing.push( 'configs' );
  missing.push( ...configs.incomplete.map( type => `config:${type}` ) );
  // Menu widgets are rendered from these server-authoritative payloads. Fetch
  // them while the loading screen is still visible so their timers never
  // appear a few seconds after the event icons.
  const [ tasks, challenge, tower ] = await Promise.all( [
    DailyTasksService.fetch( true ),
    DailyChallengeService.fetch( true ),
    WinStreakTowerService.fetch( true ),
    TournamentService.fetch( true ),
    KeyHuntService.fetch( true ),
  ] );
  if ( !tasks || DailyTasksService.fetchedAgoMs() > FRESH_FETCH_MS ) missing.push( 'daily_tasks' );
  if ( !challenge || DailyChallengeService.fetchedAgoMs() > FRESH_FETCH_MS ) missing.push( 'daily_challenge' );
  if ( !tower || WinStreakTowerService.fetchedAgoMs() > FRESH_FETCH_MS ) missing.push( 'win_streak_tower' );
  StartupProgress.netStep( 'events' );
  if ( !TestLevelBridge.isLocalLevelsForced() )
  {
    const restored_from_cache = LevelPackService.restoreCachedForFastStart();
    const refresh = LevelPackService.refresh();
    if ( !restored_from_cache || first_bootstrap )
      await refresh;

    const warmup = refresh
      .then( () => LevelPackService.ensureLevel( StatisticsStorage.load().level ) )
      .then( () => LevelPackService.prefetchOfflineWindow( StatisticsStorage.load().level ) );
    if ( first_bootstrap )
    {
      await warmup.catch( e => console.warn( "[LevelPack] startup level warmup failed", e ) );
      if ( !LevelPackService.isLevelCached( StatisticsStorage.load().level ) ) missing.push( 'levels' );
      else CrashReporter.checkpoint( 'levels_ready' );
    }
    else
      void warmup
        .then( () => CrashReporter.checkpoint( 'levels_ready' ) )
        .catch( e => console.warn( "[LevelPack] startup level warmup failed", e ) );
    void DailyChallengeService.prefetchStageLevels();

    if ( !LevelPackService.isActive() && !areBundledLevelsAllowed() )
      throw new Error( "[LevelPack] no server release available — startup is not complete" );
  }

  if ( !NetworkManager.isOnline() ) missing.push( 'network' );
  if ( missing.length === 0 )
    OfflineMode.markBootstrapComplete();
  else if ( first_bootstrap )
    throw new BootstrapIncompleteError( missing );
  else
    console.warn( `[Startup] bootstrap incomplete, running on cached data: ${missing.join( ', ' )}` );

  void LeaderboardSyncService.flushPendingScore();
  syncBannerVisibilityWithCurrentScreen();
  void AppVersionController.requestAndWait().then( () => maybeShowUpdateDialog() );
}

let resolveStartupReady: () => void = () => {};
const startup_ready = new Promise<void>( resolve => { resolveStartupReady = resolve; } );
// Offline start, or a retry that starts on what loaded: nothing more to wait for.
void startup_ready.then( () => StartupProgress.netAll() );

async function loadInitialNetworkDataWithRetry(): Promise<void>
{
  let delay_ms = 2_000;
  let routing_rechecked = false;

  for ( let attempt = 1; ; attempt++ )
  {
    if ( !NetworkManager.isOnline() && OfflineMode.canStartOffline() )
    {
      OfflineMode.enterOfflineStartup();
      resolveStartupReady();
    }
    await NetworkManager.waitForOnline();
    try
    {
      if ( attempt === 1 )
        void ServerRoutingService.check( 'startup' );
      await performLoginAndRefreshAdsConfig();
      if ( attempt > 1 )
      {
        console.info( `[LoadRetry] initial network data loaded on attempt ${attempt}` );
        NetworkManager.clearLoadingError();
      }
      resolveStartupReady();
      return;
    }
    catch ( error )
    {
      if ( error instanceof NetworkError || !NetworkManager.isOnline() )
      {
        NetworkManager.handleNetworkError( error );
        if ( OfflineMode.canStartOffline() )
        {
          OfflineMode.enterOfflineStartup();
          resolveStartupReady();
        }
      }
      else if ( error instanceof BootstrapIncompleteError )
      {
        console.warn( `[LoadRetry] ${error.message} — starting on what loaded, retrying in ${BOOTSTRAP_RETRY_MS / 1000}s` );
        resolveStartupReady();
      }
      else if ( OfflineMode.startedOffline() )
        console.warn( '[LoadRetry] initial network data failed while playing offline-started session', error );
      else
        NetworkManager.handleLoadingDataError( error );

      // The current server may be gone for good: ask the home server once
      // whether we were redirected before retrying against a dead address.
      if ( !routing_rechecked && NetworkManager.isOnline() )
      {
        routing_rechecked = true;
        await ServerRoutingService.check( 'login_failed' );
      }

      await NetworkManager.waitForOnline();
      const retry_ms = error instanceof BootstrapIncompleteError ? BOOTSTRAP_RETRY_MS : delay_ms;
      await new Promise( resolve => window.setTimeout( resolve, retry_ms ) );
      delay_ms = Math.min( delay_ms * 2, 15_000 );
    }
  }
}

window.addEventListener( "load", async () =>
{
  LogOverlay.startCapture();
  preventCollapse();

  try
  {
    await VersionLoader.load();
    await NetworkManager.init();
    new SocketHandler();
    bindAppsFlyerCustomerUserId();

    const analytics_ready = ( async (): Promise<void> =>
    {
      let consent_allowed = true;
      if ( MaxAds.isNative() )
      {
        const privacy_ready = await MaxAds.ensureLazyInit();
        consent_allowed = privacy_ready
          && S2SEventContextService.getConsentSnapshot().analytics_allowed;
      }
      CrashReporter.checkpoint( 'consent_resolved' );
      CrashIdentity.setConsentAllowed( consent_allowed );
      await initAnalytics( { enabled: true } );
      if ( !consent_allowed )
        MaxAds.onLateInit( applyLateConsent );
      await NotificationManager.consumeLaunchEvents();
      void logAfAppOpen( globalThis.__launchedFromPush ? 'push_notification' : 'icon' );
      startSessionMilestoneLoop();
    } )().catch( error => console.warn( '[Startup] analytics init failed', error ) );

    // // @ts-ignore
    // import( '@capacitor/haptics' ).then( ( { Haptics } ) =>
    // {
    //   if ( window.Capacitor && window.Capacitor.Plugins )
    //   {
    //     window.Capacitor.Plugins.Haptics = Haptics;
    //   }
    // } ).catch( error =>
    // {
    //   console.error( 'Failed to import Capacitor Haptics plugin:', error );
    // } );

    // VConsoleManager.initEruda();

    const appStartTs = performance.now();
    // void DevToDev.appStart();

    let firstScreenReported = false;
    // const origShow = ( ScreenManager as any ).show?.bind( ScreenManager );
    // if ( origShow )
    // {
    //   ( ScreenManager as any ).show = function ( ...args: any[] )
    //   {
    //     if ( !firstScreenReported )
    //     {
    //       firstScreenReported = true;
    //       const dt = performance.now() - appStartTs;
    //       // void DevToDev.firstScreenShown( dt );
    //     }
    //     return origShow( ...args );
    //   };
    // }
    await TelegramAPISettings.init();
    await Game.initializeSafeAreas();
    LocalizationManager.setRemoteReadyPromise(
      LocalizationManager.loadRemote()
        .catch( error => console.warn( '[Startup] remote localization failed', error ) )
    );

    const detected_lang =
      LocalizationManager.getSavedLanguage()
      ?? LocalizationManager.resolveSupportedLanguage( TelegramAPISettings.getUserLanguage() )
      ?? LocalizationManager.detectLanguageFromNavigator();
    if ( detected_lang )
      LocalizationManager.setLanguage( detected_lang );

    // Headless batches (stability / simulation) must not depend on the actual
    // window: hidden or zero-sized viewports (0×0 breaks ballistics with NaNs)
    // and differently-sized monitors would change camera/cannon geometry and
    // make results non-reproducible. Fixed portrait size for every batch tab.
    const headless_batch = TestLevelBridge.isBatchActive();
    const telegram_metrics = TelegramAPISettings.getScreenMetrics();
    const viewport_w = Math.round( visualViewport?.width ?? telegram_metrics.width );
    const viewport_h = Math.round( telegram_metrics.height );

    const game = new Game(
      headless_batch || viewport_w < 50 ? 390 : viewport_w,
      headless_batch || viewport_h < 50 ? 844 : viewport_h
    );
    // Before game.init() so the loading screen and the first menu frames are recorded too.
    PerfSession.maybeAutostart();

    TelegramAPISettings.subscribeToLayoutChanges( () => game.updateTelegramLayout() );
    setInterval( preventCollapse, 400 );

    try
    {
      const cap: any = ( window as any ).Capacitor;
      const appPlugin = cap?.Plugins?.App;
      if ( appPlugin?.addListener )
      {
        void appPlugin.addListener( 'appStateChange', ( state: { isActive: boolean } ) =>
        {
          if ( state?.isActive )
          {
            handleAppForeground();
            void audioController.resumeFromForeground();
            void NotificationManager.onForeground();
          }
          else
          {
            handleAppBackground();
            void audioController.suspendForBackground();
          }
        } );
      }

      document.addEventListener( 'visibilitychange', () =>
      {
        if ( document.hidden )
        {
          handleAppBackground();
          void audioController.suspendForBackground();
        }
        else
        {
          handleAppForeground();
          void audioController.resumeFromForeground();
          void NotificationManager.onForeground();
        }
      } );

      window.addEventListener( 'nativeAudioFocusChange', ( ev: any ) =>
      {
        const state = ev?.detail?.state as string;
        if ( !state ) return;
        if ( state === 'duck' )
        {
          audioController.duck();
        }
        else if ( state === 'gain' )
        {
          audioController.unduck();
          void audioController.resumeFromForeground();
        }
        else
        {
          void audioController.suspendForBackground();
        }
      } );
    }
    catch ( e )
    {
      console.warn( 'Lifecycle audio setup failed', e );
      crashCapture( e, { phase: 'audio_lifecycle' } );
    }

    game.initBackButton();
    let menu_shown_reported = false;
    Renderer.setRecreateGate( () => ScreenManager.getCurrentScreen() !== Screens.GAME );
    ScreenManager.addOnChangeListener( next =>
    {
      if ( next !== Screens.GAME )
        Renderer.flushPendingProfile();
      MaxAds.setBannerVisible( next !== Screens.GAME );
      CrashReporter.breadcrumb( 'screen', String( next ?? 'none' ) );
      if ( next === Screens.MENU && !menu_shown_reported )
      {
        menu_shown_reported = true;
        CrashReporter.checkpoint( 'menu_shown' );
      }
      if ( next === Screens.GAME )
        CrashReporter.checkpoint( 'level_started' );
    } );
    CrashReporter.setScreenProvider( () => ScreenManager.getCurrentScreen() );
    MemoryProbe.start();
    MemoryDashboard.restore();
    ( window as any ).memDebug = {
      last: () => MemoryProbe.last(),
      history: () => MemoryProbe.history(),
      previousRun: () => MemoryProbe.previousRun(),
      dashboard: () => MemoryDashboard.toggle(),
    };
    ( window as any ).uiPerf = {
      rec: ( seconds?: number ) => UiPerfProbe.rec( seconds ),
      stop: () => UiPerfProbe.stop(),
      bisect: ( segment_s?: number ) => UiPerfProbe.bisect( segment_s ),
      arm: ( seconds?: number ) => UiPerfProbe.armOnNextScreen( seconds ),
      bench: () => UiPerfProbe.canvasBench(),
      report: () => UiPerfProbe.report(),
      knobs: ( knobs: Record<string, unknown> ) => UiPerfProbe.setKnobs( knobs ),
      reset: () => UiPerfProbe.resetKnobs(),
      panel: () => UiPerfProbe.togglePanel(),
      root: () => Game.instance.currentScreenUi( ScreenManager.getCurrentScreen() ),
    };
    ( window as any ).perfSession = {
      start: () => PerfSession.start(),
      stop: () => PerfSession.stop(),
      mark: ( text?: string ) => PerfSession.mark( text ),
      snapshot: () => PerfSession.snapshot(),
      report: () => PerfSession.report(),
      copy: ( full?: boolean ) => PerfSession.copy( full ),
      save: ( full?: boolean ) => PerfSession.save( full ),
      panel: () => PerfSession.showPanel(),
      autostart: ( on: boolean ) => PerfSession.setAutostart( on ),
    };
    CrashReporter.setSnapshotProvider( () => ( {
      mem: MemoryProbe.forSnapshot(),
      screen: ScreenManager.getCurrentScreen(),
      level: StatisticsStorage.load().level,
      release_uid: LevelPackService.getReleaseUid(),
      release_version: LevelPackService.getReleaseVersion(),
      config: ConfigHashStore.fingerprint(),
      uptime_ms: Math.round( performance.now() ),
      foreground_ms: is_app_backgrounded ? null : Date.now() - foregrounded_at_ms,
      online: NetworkManager.isOnline(),
      offline_playable: OfflineMode.isPlayable(),
      journal_len: OfflineJournal.count(),
      backgrounded: is_app_backgrounded,
    } ) );
    void ensureDeviceId().then( id => CrashIdentity.setDevice( id ) ).catch( () => {} );
    try { CrashIdentity.setAnalyticsId( ensureUserId() ); } catch {}
    if ( !TestLevelBridge.isLocalLevelsForced() )
      LevelPackService.restoreCachedForFastStart();
    NetworkManager.setOfflinePlayableCheck( () => OfflineMode.isPlayable() );
    registerConfigSync();
    ( window as any ).offlineDebug = {
      configHashes: () => ConfigHashStore.getAll(),
      bootstrapped: () => OfflineMode.isBootstrapped(),
      resetBootstrap: () => OfflineMode.resetBootstrap(),
      configSync: ( reason = 'debug' ) => ConfigSyncService.refreshStale( reason ),
      configSyncAll: ( reason = 'debug' ) => ConfigSyncService.refreshAll( reason ),
      journal: () => OfflineJournal.list(),
      clearJournal: () => OfflineJournal.clear(),
      sync: ( reason = 'debug' ) => OfflineSyncService.sync( reason ),
      isOnline: () => NetworkManager.isOnline(),
      simulateOffline: () => NetworkManager.handleNetworkError( new Error( 'debug offline' ) ),
      cachedAhead: () => LevelPackService.countCachedAhead( StatisticsStorage.load().level ),
      prefetch: () => LevelPackService.prefetchOfflineWindow( StatisticsStorage.load().level ),
      play: ( level: number ) => ScreenManager.showScreen( Screens.GAME, { level } ),
      gateway: EconomyGateway,
      dailyTasks: DailyTasksService,
      tower: WinStreakTowerService,
      challenge: DailyChallengeService,
      probe: () => NetworkManager.retryByUser(),
      stats: () => StatisticsStorage.load(),
    };
    ( window as any ).tournamentDebug = {
      addCrowns: ( count = 10 ) => { TournamentService.addCrownsForDebug( count ); MenuScreen.active?.refreshTournamentProgress(); return TournamentService.debugSummary(); },
      resetCrowns: () => { TournamentService.resetCrownsForDebug(); MenuScreen.active?.refreshTournamentProgress(); return TournamentService.debugSummary(); },
      state: () => TournamentService.debugSummary(),
      /** Opens the tournament dialog as the lobby would. */
      open: async () =>
      {
        const [ { DialogManager }, { TournamentDialog } ] = await Promise.all( [
          import( "./ui/dialog/dialog_manager.js" ),
          import( "./ui/dialog/tournament_dialog.js" ),
        ] );
        const dialog = new TournamentDialog( TournamentService.isFinished() );
        DialogManager.openDialog( dialog );
        return dialog;
      },
      /** Opens a checkpoint reward overlay without a server claim; returns it, so `.claim()` plays the fly to the HUD. */
      rewardOverlay: async () =>
      {
        const [ { OverlayFactory }, { OverlayId } ] = await Promise.all( [
          import( "./ui/overlay/overlay_factory.js" ),
          import( "./ui/overlay/overlay_ids.js" ),
        ] );
        return OverlayFactory.openOverlay( OverlayId.TOURNAMENT_REWARD_OVERLAY, { reward: { type: "fireworks", amount: 3 } } );
      },
      /** Opens the grand prize chest overlay as the lobby would once every checkpoint is collected. */
      grandPrize: async () =>
      {
        const [ { OverlayFactory }, { OverlayId } ] = await Promise.all( [
          import( "./ui/overlay/overlay_factory.js" ),
          import( "./ui/overlay/overlay_ids.js" ),
        ] );
        OverlayFactory.openOverlay( OverlayId.TOURNAMENT_WIN_OVERLAY, {
          grand_prize: true,
          rewards: TournamentService.getGrandPrizeRewards(),
          on_claim: () => TournamentService.claimGrandPrize(),
        } );
      },
      service: TournamentService,
    };
    ( window as any ).offersDebug = { service: OfferService, analytics };
    ( window as any ).authDebug = {
      link: ( provider: 'google' | 'facebook' | 'apple' ) => linkProvider( fromAuthProvider( provider ) ?? LinkedSocialProvider.Google ),
      linked: () => getLinkedSocialProviders().map( p => LinkedSocialProvider[ p ] ),
      available: () => ( [ LinkedSocialProvider.Google, LinkedSocialProvider.Facebook, LinkedSocialProvider.Apple ] as LinkedSocialProvider[] )
        .map( p => `${ LinkedSocialProvider[ p ] }: ${ isProviderAvailable( p ) }` ),
      openSave: () => DialogManager.openDialog( new SaveDialog() ),
      openAccount: () => DialogManager.openDialog( new AccountProgressSavedDialog() ),
      previewConflict: ( existing_provider: 'google' | 'facebook' | 'apple' = 'google' ) =>
      {
        const me = StatisticsStorage.load();
        const now = Date.now();
        const current = { id: AuthSessionStore.getPlayerId() ?? 0, player_level: me.level, points: me.points, avatar_id: Number( localStorage.getItem( 'player_avatar_id' ) ?? 0 ), created_at: new Date( now - 3 * 86_400_000 ).toISOString(), linked_providers: [] } as any;
        const existing = { id: 482910, player_level: 42, points: 57, avatar_id: 3, created_at: new Date( now - 128 * 86_400_000 ).toISOString(), linked_providers: [ { provider: existing_provider } ] } as any;
        DialogManager.openDialog( new TwoAccountsDialog( {
          current_player: current, current_provider: currentAccountProvider( current ),
          existing_player: existing, existing_provider: existing_provider as AuthProvider,
          on_choose: choice => console.log( '[authDebug] chose', choice ),
        } ) );
      },
      previewError: ( reason: 'unavailable' | 'disabled' | 'network' | 'generic' = 'generic' ) =>
        DialogManager.openDialog( AuthMessageDialog.forLinkError( LinkedSocialProvider.Google, reason ) ),
    };
    ( window as any ).hapticsDebug = {
      state: () => ( {
        enabled: hapticsController.isHapticsEnabled(),
        mode: hapticsController.getVibrationMode(),
      } ),
      support: () => hapticsController.getSupportBreakdown(),
      log: () => hapticsController.getLog(),
      skipCounts: () => hapticsController.getSkipCounts(),
      clearLog: () => hapticsController.clearLog(),

      test: ( pattern: HapticPattern = HapticPattern.MEDIUM, context: VibrationContext = VibrationContext.GENERAL ) =>
        hapticsController.vibrate( pattern, context ),
    };
    ( window as any ).leadersDebug = {
      /** Fetches the board once and prints the full server JSON (normal logs show only a summary). */
      dump: async ( limit?: number, id = "global_0" ) =>
      {
        const [ { getLeaderboard }, { LeaderboardAuthService }, { LEADERBOARD_LIMIT } ] = await Promise.all( [
          import( "./api/leaderboard/leaderboard.requests.js" ),
          import( "./api/leaderboard/leaderboard_auth_service.js" ),
          import( "./api/leaderboard/leaderboard_board_service.js" ),
        ] );
        await LeaderboardAuthService.ensureLoggedIn();
        const res = await getLeaderboard( id, limit ?? LEADERBOARD_LIMIT );
        console.log( `[Leaders] dump\n${JSON.stringify( res, null, 2 )}` );
        return { users: res.users?.length ?? 0 };
      },
      /** Shows a fake board of `n` players on the open leaderboard; the player is at rank `me` (0 = absent). */
      fake: async ( n = 1000, me = 510 ) =>
      {
        const [ { LeaderboardScreen }, { getLeaderboardCurrentUserId } ] = await Promise.all( [
          import( "./ui/screens/leaderboard/leaderboard_screen.js" ),
          import( "./api/leaderboard/leaderboard_player_context.js" ),
        ] );
        const screen = LeaderboardScreen.current();
        if ( !screen ) return "open the leaderboard first: showScreen('leaderboard')";
        const countries = [ "UA", "US", "BR", "TR", "DE", "PL", "CZ", "MD", "ES", "IL", "FR", "IN" ];
        const users = Array.from( { length: n }, ( _, i ) => ( {
          userId: 100000 + i,
          deviceId: i + 1 === me ? getLeaderboardCurrentUserId() : `fake_${i}`,
          country: countries[i % countries.length],
          name: i + 1 === me ? "Me" : `player_${i + 1}`,
          level: 1 + ( ( i * 37 ) % 900 ),
          coins: 0,
          avatar: i % 20,
          isOnline: false,
          ratingScore: Math.max( 0, 100000 - i * 97 ),
          ratingPosition: i + 1,
        } ) );
        screen.debugShowPlayers( users );
        return screen.debugStats();
      },
      /**
       * Who scrolls the list: "js" (the bounded row pool and movement update together; default)
       * or "native" (experimental browser scrolling). Applies to the next screen built.
       */
      scrollMode: ( mode?: "native" | "js" ) =>
      {
        const key = "bb_leaderboard_scroll_mode";
        if ( mode === "js" ) localStorage.removeItem( key );
        else if ( mode === "native" ) localStorage.setItem( key, "native" );
        return { mode: localStorage.getItem( key ) === "native" ? "native" : "js", note: mode ? "reload the page to rebuild the screen" : undefined };
      },
      /** Scrolls the open list so `rank` is the top row. */
      scroll: async ( rank: number ) =>
      {
        const { LeaderboardScreen } = await import( "./ui/screens/leaderboard/leaderboard_screen.js" );
        const screen = LeaderboardScreen.current();
        screen?.debugScrollToIndex( rank - 1 );
        return screen?.debugStats() ?? null;
      },
      /** The board the screen would show now: size, complete (full vs head), live (this session) and age. */
      board: async () =>
      {
        const { LeaderboardBoardService } = await import( "./api/leaderboard/leaderboard_board_service.js" );
        const board = LeaderboardBoardService.get();
        return board && { users: board.users.length, complete: board.complete, live: board.live, age_s: Math.round( ( Date.now() - board.fetched_at ) / 1000 ), fresh: LeaderboardBoardService.isFresh() };
      },
      /**
       * Players of one country from the global board (`global_0`, up to `limit`), filtered here on
       * the client: a console table with the place on the global board and within the country.
       */
      byCountry: async ( country?: string, limit?: number ) =>
      {
        const [ { getLeaderboard }, { LeaderboardAuthService }, { LEADERBOARD_LIMIT }, { GLOBAL_BOARD_ID, normalizeCountry, playerCountry } ] = await Promise.all( [
          import( "./api/leaderboard/leaderboard.requests.js" ),
          import( "./api/leaderboard/leaderboard_auth_service.js" ),
          import( "./api/leaderboard/leaderboard_board_service.js" ),
          import( "./api/leaderboard/leaderboard_country.js" ),
        ] );
        const cc = normalizeCountry( country ) ?? playerCountry();
        if ( !cc ) return "no country: pass one, e.g. leadersDebug.byCountry('UA')";
        await LeaderboardAuthService.ensureLoggedIn();
        const res = await getLeaderboard( GLOBAL_BOARD_ID, limit ?? LEADERBOARD_LIMIT );
        const all = res.users ?? [];
        const seen = new Set<number>();
        const rows = all
          .filter( u => normalizeCountry( u.country ) === cc && !seen.has( u.userId ) && seen.add( u.userId ) )
          .sort( ( a, b ) => a.ratingPosition - b.ratingPosition )
          .map( ( u, i ) => ( { place: i + 1, global_place: u.ratingPosition, name: u.name, stars: u.ratingScore, level: u.level, player_id: u.deviceId, userId: u.userId } ) );
        console.log( `[Leaders] ${cc}: ${rows.length} of ${all.length} players on ${GLOBAL_BOARD_ID}` );
        console.table( rows );
        return { country: cc, players: rows.length, board: all.length };
      },
      /** Country tab: the player's country, its board (`country:<code>`) and its state after the catch-up. */
      country: async () =>
      {
        const [ { LeaderboardCountryBoardService }, { LeaderboardSyncService }, { playerCountry, countryBoardId } ] = await Promise.all( [
          import( "./api/leaderboard/leaderboard_board_service.js" ),
          import( "./api/leaderboard/leaderboard_sync_service.js" ),
          import( "./api/leaderboard/leaderboard_country.js" ),
        ] );
        const country = playerCountry();
        if ( !country ) return { country: null, note: "no player_country in localStorage: the tab shows 'soon'" };
        await LeaderboardSyncService.countryReady();
        await LeaderboardCountryBoardService.refresh().catch( e => console.warn( e ) );
        const board = LeaderboardCountryBoardService.get();
        const me = board?.users.find( u => u.deviceId === localStorage.getItem( "player_id" ) );
        return {
          country,
          id: countryBoardId( country ),
          players: board?.users.length ?? 0,
          complete: board?.complete ?? false,
          my_place: me?.ratingPosition ?? null,
          my_stars: me?.ratingScore ?? null,
        };
      },
      /** Forgets the board (memory and storage): the next open goes the no-cache way (head first). */
      reset: async () =>
      {
        const { LeaderboardBoardService } = await import( "./api/leaderboard/leaderboard_board_service.js" );
        LeaderboardBoardService.reset();
      },
      /** What the menu does in the background: login + board, throttled. */
      prefetch: async () =>
      {
        const { LeaderboardBoardService } = await import( "./api/leaderboard/leaderboard_board_service.js" );
        await LeaderboardBoardService.prefetch();
        return LeaderboardBoardService.get()?.users.length ?? 0;
      },
      /** Taps the Down/Up pill under the list. */
      jump: async () =>
      {
        const { LeaderboardScreen } = await import( "./ui/screens/leaderboard/leaderboard_screen.js" );
        LeaderboardScreen.current()?.debugJump();
      },
      /** Pool rows built/bound, nodes and canvases in the list layer, current offset. */
      stats: async () =>
      {
        const { LeaderboardScreen } = await import( "./ui/screens/leaderboard/leaderboard_screen.js" );
        return LeaderboardScreen.current()?.debugStats() ?? null;
      },
    };
    ( window as any ).menuLayoutDebug = {
      get: () => MenuLayoutConfigService.get(),
      apply: ( raw: unknown ) => { MenuLayoutConfigService.apply( normalizeMenuLayout( raw ) ); return MenuLayoutConfigService.get(); },
      reset: () => { MenuLayoutConfigService.apply( DEFAULT_MENU_LAYOUT ); return MenuLayoutConfigService.get(); },
      refresh: () => MenuLayoutConfigService.refresh(),
    };
    ( window as any ).earlyEcpmDebug = {
      state: async () => ( await import( "./ads/early_ecpm.js" ) ).earlyEcpmSnapshot(),
      flush: async () => ( await import( "./ads/early_ecpm.js" ) ).flushEarlyEcpm(),
      reset: async () => ( await import( "./ads/early_ecpm.js" ) ).resetEarlyEcpm(),
      load: async ( ecpm = 25, format = 'Interstitial' ) =>
      {
        const module = await import( "./ads/early_ecpm.js" );
        module.recordAdLoaded( { format, revenue: ecpm / 1000, sourceName: 'debug', revenuePrecision: 'publisher_defined' } );
        return module.earlyEcpmSnapshot();
      },
    };
    ( window as any ).graphicsDebug = {
      get: () => GraphicsSettings.get(),
      set: ( quality: GraphicsQuality ) => GraphicsSettings.set( quality ),
      describe: () => GraphicsSettings.describe(),
      override: ( partial: Partial<GraphicsProfile> | null ) => GraphicsSettings.setOverride( partial ),
      msaa: () => Renderer.hasMsaa(),
      remote: () => GraphicsConfigService.describe(),
      refreshRemote: () => GraphicsConfigService.refresh(),
      applyRemote: ( raw: unknown ) => { GraphicsConfigService.apply( normalizeGraphicsConfig( raw ) ); return GraphicsSettings.describe(); },
      pendingRecreate: () => Renderer.hasPendingProfile(),
      backdrop: ( visible: boolean ) =>
      {
        let n = 0;
        SceneManager.world?.traverse( o =>
        {
          if ( ( o as any ).isMesh && o.renderOrder <= -1000 && o.renderOrder >= -1100 )
          {
            o.visible = visible;
            n++;
          }
        } );
        return n;
      },
      scene: () =>
      {
        const lights: unknown[] = [];
        let meshes = 0, with_normal_map = 0, receive_shadow = 0;
        let catcher_visible: boolean | null = null;
        SceneManager.world?.traverse( o =>
        {
          const a = o as any;
          if ( a.isLight )
          {
            lights.push( { name: o.name, type: o.type, visible: o.visible, intensity: a.intensity, shadow_map: a.castShadow ? a.shadow?.mapSize?.width : null } );
            return;
          }
          if ( !a.isMesh ) return;
          meshes++;
          const mats = Array.isArray( a.material ) ? a.material : [ a.material ];
          if ( mats.some( ( m: any ) => m && m.normalMap ) ) with_normal_map++;
          if ( a.receiveShadow ) receive_shadow++;
          if ( mats.some( ( m: any ) => m && m.isShadowMaterial ) ) catcher_visible = o.visible;
        } );
        const r = Renderer.renderer;
        return {
          lights, meshes, with_normal_map, receive_shadow, catcher_visible,
          shadows: r.shadowMap.enabled, shadow_type: r.shadowMap.type,
          pixel_ratio: r.getPixelRatio(), buffer: [ r.domElement.width, r.domElement.height ],
          ui_pixel_ratio: Game.getDpr(), ui_buffer: ( () => { const m = document.getElementById( "menuCanvas" ) as HTMLCanvasElement | null; return m ? [ m.width, m.height ] : null; } )(),
          ball_shadow: CannonBall.cast_shadow,
          renderer_canvases: document.querySelectorAll( "canvas:not([id])" ).length,
        };
      },
    };
    ( window as any ).backgroundWork = { describe: () => BackgroundWork.describe() };
    ( window as any ).transportDebug = {
      describe: () => TransportFlags.describe(),
      use: ( format: 'json' | 'msgpack' | null ) => TransportFlags.setOverride( format ),
      bootstrap: () => fetchBootstrap( ConfigSyncService.knownHashes(), ConfigSyncService.registeredTypes() ),
      primed: () => PrimedResponses.size(),
    };
    NetworkManager.addOnlineListener( () => { void OfflineSyncService.sync( 'online' ); } );
    NetworkManager.addOnlineListener( () => { void LeaderboardSyncService.flushPendingScore(); } );
    OfflineJournal.subscribe( pending =>
    {
      if ( pending > 0 && NetworkManager.isOnline() && !OfflineSyncService.isReplaying() )
        void OfflineSyncService.sync( 'journal' );
    } );
    LocalMetrics.addSink( events => DailyTasksService.applyLocalProgress( events ) );
    LocalMetrics.addSink( events => KeyHuntService.applyLocalProgress( events ) );
    OfflineMode.startHeartRegenTicker();
    setOnUnauthorized( async () => { await performLogin(); } );
    const initial_network_data = loadInitialNetworkDataWithRetry();
    game.setMenuStartupReadyPromise( startup_ready );
    LevelPackService.setStartupReadyPromise(
      TestLevelBridge.isLocalLevelsForced() ? Promise.resolve() : startup_ready
    );
    void game.init().catch( e => crashCapture( e, { phase: 'game_init' } ) );

    const game_loaded_at = new Date().toISOString();
    void analytics_ready.then( () =>
      trackWebEvent( 'game_loaded', { timestamp: game_loaded_at } ) );

    DebugConsoleManager.attachSecretCodeListener();
    if ( DebugSettings.SHOW_DEBUG_CONSOLE_ENTRY_BUTTON )
      DebugConsoleManager.attachEntryButton();
    void initial_network_data.then( () =>
    {
      ServerRoutingService.markStartupComplete();
      reportSessionToServer( true );
      void analytics_ready.then( () => syncAppsflyerAttribution() );
    } );

    setTimeout( () =>
    {
      void NotificationManager.init().then( () => NotificationManager.onForeground() );
    }, 2000 );
  }
  catch ( e: any )
  {
    console.error( 'App init failed', e );
    crashCapture( e, { phase: 'game_load' }, false );
    throw e;
  }

} );
